import http from "@/utils/axios.js";
import { notify } from "@kyvg/vue3-notification";
import {
  removeFromLocalStorage,
  saveToLocalStorage,
} from "@/utils/localstorage";

export default {
  namespaced: true,
  state: {
    patient: {
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      password_confirm: "",
      code: "",
      phone_number: "",
      date: "",
      pesel: "",
      birth_date: null,
      isNoPesel: false,
      accept_terms: false,
      accept_marketing: false,
      gender: "",
    },
    errors: [],
    isSubmittingForm: false,
  },
  getters: {
    isSubmittingForm: (state) => state.isSubmittingForm,
    getPatient: (state) => state.patient,
    getErrors: (state) => state.errors,
  },
  mutations: {
    setPatient(state, patient) {
      Object.assign(state.patient, patient);
    },
    setIsSubmittingForm(state, isSubmittingForm) {
      state.isSubmittingForm = isSubmittingForm;
    },
    setLoginToken(state, token) {
      saveToLocalStorage("authtoken", token);
      http.defaults.headers["Authorization"] = "Bearer " + token;
    },
    resetPatient(state) {
      state.patient = {
        first_name: "",
        last_name: "",
        email: "",
        password: "",
        password_confirm: "",
        code: "",
        phone_number: "",
        date: "",
        pesel: "",
        birth_date: null,
        isNoPesel: false,
        accept_terms: false,
        accept_marketing: false,
        gender: "",
      };
    },
    setErrors(state, errors) {
      state.errors = errors;
    },
    clearErrors(state) {
      state.errors = [];
    },
  },
  actions: {
    async logout({ commit, dispatch }) {
      try {
        await http.post("/logout");
        dispatch("resetUserAfterLogOut");
      } catch (error) {
        this.errors = error.response.data.errors;
      }
    },
    resetUserAfterLogOut({ commit }) {
      removeFromLocalStorage("authtoken");
      removeFromLocalStorage("cart");
      commit("resetPatient");
      commit("user/resetCurrentUser", null, { root: true });
      commit("cart/clearAll", null, { root: true });
      commit("checkout/clearData", null, { root: true });
      commit("checkout/clearPatients", null, { root: true });
      commit("checkout/clearPersonalData", null, { root: true });
    },
    async loginPatient({ state, dispatch }) {
      try {
        return await dispatch("loginUser", state.patient);
      } catch (error) {
        console.log(error);
        throw Error();
      }
    },
    async loginUser({ commit, dispatch }, credentials) {
      try {
        const { data } = await http.post("/patient/login", credentials);

        commit("setLoginToken", data.token);
        await dispatch("prepareDataForUser", data.user);
        await dispatch("user/assignQuizToUser", null, { root: true });
        commit("clearErrors");

        return data;
      } catch ({ response }) {
        if (response && response.status === 422) {
          commit("setErrors", response.data.errors);
        }
        throw Error();
      }
    },
    async prepareDataForUser({ commit, dispatch, rootGetters }, user) {
      commit("user/setCurrentUser", user, { root: true });

      await dispatch("user/fetchAccountData", null, { root: true });

      if (user.default_facility && !rootGetters["cart/getSelectedFacility"]) {
        commit("cart/setSelectedFacility", user.default_facility, {
          root: true,
        });
      }
    },
    async registerPatient(
      { commit, state, dispatch },
      withValidateUser = false
    ) {
      return new Promise((resolve, reject) => {
        return http
          .post("/patient/send-verification-token", {
            ...state.patient,
            withValidateUser,
          })
          .then(async ({ data }) => {
            commit("clearErrors");
            commit("setLoginToken", data.token);
            await dispatch("prepareDataForUser", data.user);
            resolve(data);
          })
          .catch(({ response }) => {
            if (response && response.status === 422)
              commit("setErrors", response.data.errors);
            reject();
          });
      });
    },
    async fullRegisterPatient({ commit }, userData) {
      return new Promise((resolve, reject) => {
        return http
          .post("/patient/register", userData)
          .then(({ data }) => {
            commit("clearErrors");
            resolve(data);
          })
          .catch(({ response }) => {
            if (response && response.status === 422) {
              reject(response.data.errors);
            } else {
              reject();
            }
          });
      });
    },
    async verifyAuthUser({ commit }, userData) {
      return new Promise((resolve, reject) => {
        return http
          .post("/patient/verify-code", userData)
          .then(() => {
            commit("clearErrors");
            resolve();
          })
          .catch(({ response }) => {
            if (response && response.status === 422)
              commit("setErrors", response.data.errors);
            reject();
          });
      });
    },
    async resendCodeAuthUser({ commit }, userData) {
      commit("clearErrors");
      await http
        .post("/patient/send-verification-token", userData)
        .then(() => {
          notify({
            type: "info",
            text: "messages.code.resend",
            duration: 5000,
          });
        })
        .catch(({ response }) => {
          if (response && response.status === 422) {
            notify({
              type: "danger",
              text: "messages.code.resend_error",
              duration: 5000,
            });
            commit("setErrors", response.data.errors);
          }
          if (response && response.status === 429)
            notify({
              type: "danger",
              text: "messages.code." + response.data.errors.send_code,
              duration: 5000,
            });
        });
    },
    async completeData({ commit, state }) {
      return new Promise((resolve, reject) => {
        return http
          .post("/patient/complete-data", state.patient)
          .then(({ data }) => {
            commit("clearErrors");
            resolve(data);
          })
          .catch(({ response }) => {
            if (response && response.status === 422)
              commit("setErrors", response.data.errors);
            reject();
          });
      });
    },
  },
};
